import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  ApiResponse, PageResponse, DataSharingRequestResponse, CreateDsrRequest,
  ProcessDsrActionRequest, ServiceDocumentationAttachmentDto
} from '../models/models';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class DsrService {
  private readonly apiUrl = `${environment.apiUrl}/dsr`;

  constructor(private http: HttpClient) {}

  createDsr(request: CreateDsrRequest): Observable<ApiResponse<DataSharingRequestResponse>> {
    return this.http.post<ApiResponse<DataSharingRequestResponse>>(this.apiUrl, request);
  }

  getAllRequests(): Observable<ApiResponse<DataSharingRequestResponse[]>> {
    return this.http.get<ApiResponse<DataSharingRequestResponse[]>>(`${this.apiUrl}/all`);
  }

  getMyRequests(): Observable<ApiResponse<DataSharingRequestResponse[]>> {
    return this.http.get<ApiResponse<DataSharingRequestResponse[]>>(`${this.apiUrl}/my-request`);
  }

  getIncomingRequests(): Observable<ApiResponse<DataSharingRequestResponse[]>> {
    return this.http.get<ApiResponse<DataSharingRequestResponse[]>>(`${this.apiUrl}/incoming`);
  }

  getOutgoingRequests(): Observable<ApiResponse<DataSharingRequestResponse[]>> {
    return this.http.get<ApiResponse<DataSharingRequestResponse[]>>(`${this.apiUrl}/outgoing`);
  }

  getRequestsPaged(params?: { tab?: string; status?: string; search?: string; page?: number; size?: number }): Observable<ApiResponse<PageResponse<DataSharingRequestResponse>>> {
    const queryParts: string[] = [];
    if (params?.tab) queryParts.push(`tab=${encodeURIComponent(params.tab)}`);
    if (params?.status) queryParts.push(`status=${encodeURIComponent(params.status)}`);
    if (params?.search) queryParts.push(`search=${encodeURIComponent(params.search)}`);
    if (params?.page !== undefined) queryParts.push(`page=${params.page}`);
    if (params?.size !== undefined) queryParts.push(`size=${params.size}`);
    const query = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
    return this.http.get<ApiResponse<PageResponse<DataSharingRequestResponse>>>(`${this.apiUrl}/paged${query}`);
  }

  getDsrByUid(uid: string): Observable<ApiResponse<DataSharingRequestResponse>> {
    return this.http.get<ApiResponse<DataSharingRequestResponse>>(`${this.apiUrl}/${uid}`);
  }

  processDsrAction(uid: string, request: ProcessDsrActionRequest): Observable<ApiResponse<DataSharingRequestResponse>> {
    return this.http.post<ApiResponse<DataSharingRequestResponse>>(`${this.apiUrl}/${uid}/approve-request`, request);
  }

  testConnection(dsrUid: string): Observable<ApiResponse<DataSharingRequestResponse>> {
    return this.http.post<ApiResponse<DataSharingRequestResponse>>(`${this.apiUrl}/test-connection-request?uid=${dsrUid}`, {});
  }

  liveConnectionRequest(uid: string): Observable<ApiResponse<DataSharingRequestResponse>> {
    return this.http.post<ApiResponse<DataSharingRequestResponse>>(`${this.apiUrl}/${uid}/live-connection-request`, {});
  }

  uploadDocumentation(uid: string, doc: ServiceDocumentationAttachmentDto): Observable<ApiResponse<DataSharingRequestResponse>> {
    return this.http.post<ApiResponse<DataSharingRequestResponse>>(`${this.apiUrl}/${uid}/documentation`, doc);
  }

  removeDocumentation(uid: string, docUid: string): Observable<ApiResponse<DataSharingRequestResponse>> {
    return this.http.delete<ApiResponse<DataSharingRequestResponse>>(`${this.apiUrl}/${uid}/documentation/${docUid}`);
  }
}
